import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Invitation } from '@/api/invitations/invitationsApi.types';
import { Icon, type IconName } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import {
  useGenerateInvitationLinkMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
} from '@/hooks/useInvitationActionMutations';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { copyTextToClipboard } from '@/lib/clipboard';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface PendingInvitationsListProps {
  invitations: Invitation[];
  title: string;
  page: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

interface RevealedLink {
  invitationId: string;
  link: string;
}

export function PendingInvitationsList({
  invitations,
  title,
  page,
  totalItems,
  onPageChange,
}: PendingInvitationsListProps) {
  const { t } = useTranslation(['admin', 'common']);
  const copyLinkMutation = useGenerateInvitationLinkMutation();
  const resendMutation = useResendInvitationMutation();
  const revokeMutation = useRevokeInvitationMutation();
  const [invitationToRevoke, setInvitationToRevoke] = useState<Invitation | null>(null);
  const [revealedLink, setRevealedLink] = useState<RevealedLink | null>(null);

  const isBusy =
    copyLinkMutation.isPending || resendMutation.isPending || revokeMutation.isPending;

  function reportError(actionError: unknown) {
    const backendMessage = resolveApiErrorMessage(actionError);
    showErrorNotification(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
  }

  function revealInviteLink(invitationId: string) {
    copyLinkMutation.mutate(invitationId, {
      onSuccess: function handleLinkReady(result) {
        setRevealedLink({ invitationId, link: result.inviteLink });
        showSuccessNotification(t('admin:invitations.linkReady'));
      },
      onError: reportError,
    });
  }

  async function copyRevealedLink() {
    if (revealedLink === null) {
      return;
    }
    const didCopy = await copyTextToClipboard(revealedLink.link);
    if (didCopy === true) {
      showSuccessNotification(t('admin:invitations.linkCopied'));
      return;
    }
    showErrorNotification(t('admin:invitations.copyFailed'));
  }

  function resendInvite(invitationId: string) {
    resendMutation.mutate(invitationId, {
      onSuccess: function handleResent() {
        showSuccessNotification(t('admin:invitations.resent'));
      },
      onError: reportError,
    });
  }

  function confirmRevoke() {
    if (invitationToRevoke === null) {
      return;
    }
    revokeMutation.mutate(invitationToRevoke.id, {
      onSuccess: function handleRevoked() {
        showSuccessNotification(t('admin:invitations.revoked'));
        if (revealedLink !== null && revealedLink.invitationId === invitationToRevoke.id) {
          setRevealedLink(null);
        }
        setInvitationToRevoke(null);
      },
      onError: function handleRevokeError(revokeError) {
        reportError(revokeError);
        setInvitationToRevoke(null);
      },
    });
  }

  return (
    <div className="mt-8">
      <h2 className="text-sm font-bold text-zooindigo mb-3">{title}</h2>
      <Card padding="none" className="divide-y divide-zooborder">
        {invitations.map(function renderInvitationRow(invitation) {
          const isRevealed = revealedLink !== null && revealedLink.invitationId === invitation.id;
          return (
            <div key={invitation.id} className="px-4 sm:px-5 py-3.5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-zoowarningbg text-zoowarningtext flex items-center justify-center shrink-0">
                  <Icon name="Clock" />
                </div>
                <div className="flex-1 min-w-0 text-sm text-zooindigo truncate">
                  {invitation.email}
                </div>
                <Badge tone="warning" size="sm" className="hidden sm:inline-flex shrink-0">
                  {t('admin:invitations.statusInvited')}
                </Badge>
                <div className="flex items-center gap-1 shrink-0">
                  <InvitationActionButton
                    iconName="Link"
                    label={t('admin:invitations.copyLink')}
                    disabled={isBusy}
                    isLoading={
                      copyLinkMutation.isPending && copyLinkMutation.variables === invitation.id
                    }
                    onClick={function handleReveal() {
                      revealInviteLink(invitation.id);
                    }}
                  />
                  <InvitationActionButton
                    iconName="RefreshCw"
                    label={t('admin:invitations.resend')}
                    disabled={isBusy}
                    isLoading={
                      resendMutation.isPending && resendMutation.variables === invitation.id
                    }
                    onClick={function handleResend() {
                      resendInvite(invitation.id);
                    }}
                  />
                  <InvitationActionButton
                    iconName="Trash"
                    label={t('admin:invitations.revoke')}
                    tone="danger"
                    disabled={isBusy}
                    onClick={function handleRevoke() {
                      setInvitationToRevoke(invitation);
                    }}
                  />
                </div>
              </div>

              {isRevealed === true && revealedLink !== null ? (
                <div className="mt-3 space-y-1.5">
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={revealedLink.link}
                      aria-label={t('admin:invitations.linkLabel')}
                      className="text-xs"
                      onFocus={function selectAll(focusEvent) {
                        focusEvent.currentTarget.select();
                      }}
                    />
                    <Button size="small" variant="outline" onClick={copyRevealedLink}>
                      <Icon name="Link" /> {t('admin:invitations.copy')}
                    </Button>
                  </div>
                  <p className="text-[11px] text-zoomuted">{t('admin:invitations.linkHint')}</p>
                </div>
              ) : null}
            </div>
          );
        })}
      </Card>
      {totalItems > ADMIN_LIST_PAGE_SIZE ? (
        <ListPagination
          page={page}
          pageSize={ADMIN_LIST_PAGE_SIZE}
          totalItems={totalItems}
          onPageChange={onPageChange}
        />
      ) : null}

      {invitationToRevoke === null ? null : (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('admin:invitations.revokeConfirmTitle')}
          description={t('admin:invitations.revokeConfirmDescription', {
            email: invitationToRevoke.email,
          })}
          confirmLabel={t('admin:invitations.revoke')}
          isConfirming={revokeMutation.isPending}
          onConfirm={confirmRevoke}
          onCancel={function cancelRevoke() {
            setInvitationToRevoke(null);
          }}
        />
      )}
    </div>
  );
}

interface InvitationActionButtonProps {
  iconName: IconName;
  label: string;
  disabled: boolean;
  isLoading?: boolean;
  tone?: 'default' | 'danger';
  onClick: () => void;
}

function InvitationActionButton({
  iconName,
  label,
  disabled,
  isLoading = false,
  tone = 'default',
  onClick,
}: InvitationActionButtonProps) {
  return (
    <Button
      variant={tone === 'danger' ? 'destructiveGhost' : 'ghost'}
      size="icon"
      className="w-8 h-8"
      aria-label={label}
      title={label}
      disabled={disabled}
      isLoading={isLoading}
      onClick={onClick}
    >
      {isLoading === true ? null : <Icon name={iconName} />}
    </Button>
  );
}
